import React, { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { CircularProgress } from '@material-ui/core';
import { useQuery } from '@apollo/client';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import zipcode_to_timezone from 'zipcode-to-timezone';
import { startCase } from 'lodash';
import { genAttributes, useWarehouseAggregate } from '@onward-delivery/core';
import { greens, greys, onwardNavy } from '@/styles/colors';
import { ORDER_BY_ID } from '@/components/Accessorials/graphql';
import { FONT, KV, SectionTitle } from './detailTabBlocks';
import DetailTabItemsAndDocs from './DetailTabItemsAndDocs';
import DetailTabNotes from './DetailTabNotes';
import DetailTabSurvey from './DetailTabSurvey';
import { toNational } from '@/utilities/formatPhoneNumber';
import { asDateInTZ } from '@/utilities/convertToISO';
import { delWindowToFormattedDateRange, delWindowToFormattedDateSingle } from '@/utilities/delWindowToFormattedDate';
import { hourMinuteFormatter } from '@/constants/formats';
import { recommendedEquipmentLabels } from '@/constants/recommendedEquipmentLabels';
import { LOCATION_TYPES } from '@/constants/locationTypes';
import deliveryStrings from '@/constants/deliveryStrings';

const RAIL_BG = '#f7f7f9';

const TABS = ['Details', 'Items/Docs', 'Notes', 'Survey'];

const contactLine = css`
    font-family: ${FONT};
    font-weight: 500;
    font-size: 12px;
    color: ${greys[2]};
    padding: 2px 0;
`;

const partyName = css`
    font-family: ${FONT};
    font-weight: 700;
    font-size: 12.5px;
    color: ${greys[3]};
    padding: 6px 0 2px;
`;

// Fresh, compact Details tab — mirrors the derivations in
// Accessorials/OrderInfoSection/OrderSummaryCard but renders as condensed KV rows.
function DetailsTab({ order }) {
    const { zip: pickup_zip } = genAttributes(order, true);
    const { full_address: pu_full_address, address: pu_address, unit: pu_unit } = genAttributes(order, true);
    const {
        full_address: do_full_address,
        address: do_address,
        unit: do_unit,
        zip: dropoff_zip,
        location: do_location,
        location_info: do_location_info,
        location_type: do_location_type,
    } = genAttributes(order);

    const formatTimestamp = (datestr) => {
        if (!datestr || !pickup_zip) return 'N/A';
        const tz = zipcode_to_timezone.lookup(order[pickup_zip]) || Intl.DateTimeFormat().resolvedOptions().timeZone;
        return formatInTimeZone(asDateInTZ(datestr, tz), tz, 'EEE, MMM d, yyyy');
    };

    const timeframe = useMemo(() => {
        if (!order) return '-';
        return order.del_window_start
            ? delWindowToFormattedDateRange(order.del_window_start, order.del_window_end, order[dropoff_zip])
            : '-';
    }, [order, dropoff_zip]);

    const duration = order?.duration_seconds && hourMinuteFormatter.format(order.duration_seconds / 60);

    const otherEquipment = useMemo(() => {
        if (!order?.recommended_equipment) return {};
        const {
            ['2_man_team']: a,
            ['3_man_team']: b,
            lift_gate: c,
            ...rest
        } = Object.keys(order.recommended_equipment)
            .filter((key) => order.recommended_equipment[key])
            .reduce((acc, val) => ({ ...acc, [val]: recommendedEquipmentLabels[val] }), {});
        return rest;
    }, [order]);

    const [pickupRoute, dropoffRoute] = useMemo(() => {
        const sortedRoutes = [...(order?.routes || [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        return [
            sortedRoutes.find((mapping) => mapping.type === 'PICKUP')?.route,
            sortedRoutes.find((mapping) => mapping.type !== 'PICKUP')?.route,
        ];
    }, [order]);

    const routeLabel = useMemo(() => {
        const parts = [pickupRoute?.route_number, dropoffRoute?.route_number].filter(Boolean);
        return parts.length ? parts.join(' / ') : 'N/A';
    }, [pickupRoute, dropoffRoute]);

    const pickupLocationName = useMemo(() => {
        if (!order?.order_shipper?.locations) return '';
        const pickupLocation = order.order_shipper.locations.find(
            (location) => location.business_address === order.pickup_street_address
        );
        return pickupLocation?.location_name || '';
    }, [order]);

    const warehouse = useWarehouseAggregate(order?.wh_events || []);

    const daysInStorage = useMemo(() => {
        if (!warehouse?.received_date || !order?.completion_time) return 'N/A';
        const start = new Date(warehouse.received_date).getTime();
        const end = new Date(order.completion_time).getTime();
        return Math.round((end - start) / 1000 / 60 / 60 / 24);
    }, [order, warehouse]);

    const docCount = order?.documents?.length || 0;
    const tagsLabel = order?.tags?.length ? order.tags.map((t) => t.tag.tag).join(', ') : 'N/A';
    const serviceLevel = order?.service_level?.service_level || LOCATION_TYPES[order?.dropoff_location_type] || '--';
    const equipmentLabel = Object.values(otherEquipment).join(', ') || 'N/A';

    const pickupAddress =
        order[pu_full_address] || `${order[pu_address] || ''}${order[pu_unit] ? `, Unit ${order[pu_unit]}` : ''}`;
    const dropoffAddress =
        order[do_full_address] || `${order[do_address] || ''}${order[do_unit] ? `, Unit ${order[do_unit]}` : ''}`;
    const dropoffLocation = [
        order[do_location],
        order[do_location_info],
        order[do_location_type] ? deliveryStrings.dropOffLocationType[order[do_location_type]] : null,
    ]
        .filter(Boolean)
        .join(', ');

    return (
        <div>
            <SectionTitle first>Schedule</SectionTitle>
            <KV
                label="Created"
                value={order.created_at ? format(new Date(order.created_at), 'EEE, MMM d, yyyy') : 'N/A'}
            />
            <KV label="Available" value={order.first_available ? formatTimestamp(order.first_available) : '--'} />
            <KV label="Scheduled Delivery" value={order.delivery_date_formatted || 'TBD'} />
            <KV label="Preferred Delivery" value={order.preferred_delivery_date_formatted || 'N/A'} />
            <KV
                label="Pickup Time"
                value={
                    order.ltl_pickup_completed
                        ? `${formatTimestamp(order.ltl_pickup_completed)} ${delWindowToFormattedDateSingle(
                              order.ltl_pickup_completed,
                              order[pickup_zip]
                          )}`
                        : 'N/A'
                }
            />
            <KV
                label="Arrival Time"
                value={
                    order.completion_time
                        ? delWindowToFormattedDateSingle(order.ltl_dropoff_arrival, order[dropoff_zip])
                        : 'N/A'
                }
            />
            <KV
                label="Completed Time"
                value={
                    order.completion_time
                        ? delWindowToFormattedDateSingle(order.completion_time, order[dropoff_zip])
                        : 'N/A'
                }
            />
            <KV label="Delivery Timeframe" value={timeframe} />

            <SectionTitle>Logistics</SectionTitle>
            <KV label="Estimated Distance" value={order.distance ? `${order.distance}` : 'N/A'} />
            <KV label="Estimated Trip Time" value={duration || 'N/A'} />
            <KV label="Route" value={routeLabel} />
            <KV label="Tags" value={tagsLabel} />
            <KV label="Service Level" value={serviceLevel} />
            <KV label="Equipment" value={equipmentLabel} />
            <KV label="Attached Documents" value={docCount ? `${docCount} file${docCount > 1 ? 's' : ''}` : 'None'} />

            <SectionTitle>Warehouse</SectionTitle>
            <KV
                label="Received"
                value={warehouse?.received_date ? format(new Date(warehouse.received_date), 'EEE, MMM d, yyyy') : 'N/A'}
            />
            <KV label="Days in Storage" value={daysInStorage} />

            <SectionTitle>Pickup</SectionTitle>
            {pickupLocationName ? <div css={partyName}>{pickupLocationName}</div> : null}
            <div css={contactLine}>{toNational(order.pickup_phone) || 'Phone Not Provided'}</div>
            <div css={contactLine}>{order.pickup_email || 'Email Not Provided'}</div>
            <div css={contactLine}>{pickupAddress}</div>
            <KV
                label="First Available"
                value={order.first_available_date ? formatTimestamp(order.first_available_date) : 'TBD'}
            />

            <SectionTitle>Dropoff</SectionTitle>
            <div css={partyName}>{order.dropoff_name || '—'}</div>
            <div css={contactLine}>{toNational(order.dropoff_phone) || 'Phone Not Provided'}</div>
            <div css={contactLine}>{order.dropoff_email || 'Email Not Provided'}</div>
            <div css={contactLine}>{dropoffAddress}</div>
            {dropoffLocation ? <div css={contactLine}>{dropoffLocation}</div> : null}
        </div>
    );
}

export default function DetailPanel({ order, width = 380 }) {
    const [tab, setTab] = useState('Details');

    // Lazy, cache-first enriched fetch. Navigating back to an order already
    // viewed resolves synchronously from the Apollo cache (no refetch / spinner).
    const { data, loading } = useQuery(ORDER_BY_ID, {
        variables: { order_id: order.order_id },
        fetchPolicy: 'cache-first',
        skip: !order?.order_id,
    });
    const enriched = data?.orders?.[0];

    // Overview header reads from base order fields, which are always present.
    const lane = [order.dropoff_city, order.dropoff_state].filter(Boolean).join(', ');
    const orderTypeLabel = order.order_type ? startCase(order.order_type) : 'Order';
    const serviceLevel = enriched?.service_level?.service_level || LOCATION_TYPES[order.dropoff_location_type] || '--';

    const showSpinner = loading && !enriched;

    return (
        <div
            css={css`
                width: ${width}px;
                flex: 0 0 ${width}px;
                border-left: 1px solid ${greys[4]};
                background: ${RAIL_BG};
                display: flex;
                flex-direction: column;
                min-height: 0;
            `}
        >
            {/* Fixed overview header */}
            <div
                css={css`
                    padding: 18px 20px 14px;
                    border-bottom: 1px solid ${greys[4]};
                    background: white;
                    flex: 0 0 auto;
                `}
            >
                <div
                    css={css`
                        font-family: ${FONT};
                        font-weight: 800;
                        font-size: 16px;
                        color: ${onwardNavy};
                        letter-spacing: -0.01em;
                        line-height: 1.25;
                    `}
                >
                    {lane || '—'}
                </div>
                <div
                    css={css`
                        font-family: ${FONT};
                        font-weight: 700;
                        font-size: 12px;
                        color: ${greens[0]};
                        margin-top: 3px;
                    `}
                >
                    {orderTypeLabel}
                </div>
                <div
                    css={css`
                        margin-top: 10px;
                    `}
                >
                    <KV label="PO / Ref #" value={order.po_number || order.reference_id || '—'} />
                    <KV label="Customer" value={order.dropoff_name || '—'} />
                    <KV label="Service Level" value={serviceLevel} />
                    <KV label="Scheduled" value={order.delivery_date_formatted || 'TBD'} />
                </div>
            </div>

            {/* Tab bar */}
            <div
                css={css`
                    display: flex;
                    border-bottom: 1px solid ${greys[4]};
                    background: white;
                    flex: 0 0 auto;
                    padding: 0 8px;
                `}
            >
                {TABS.map((t) => {
                    const active = tab === t;
                    return (
                        <button
                            key={t}
                            onClick={() => setTab(t)}
                            css={css`
                                flex: 1;
                                font-family: ${FONT};
                                font-weight: 700;
                                font-size: 11.5px;
                                padding: 11px 4px;
                                cursor: pointer;
                                background: transparent;
                                color: ${active ? greens[0] : greys[1]};
                                border: none;
                                border-bottom: 2px solid ${active ? greens[0] : 'transparent'};
                                margin-bottom: -1px;
                                white-space: nowrap;
                            `}
                        >
                            {t}
                        </button>
                    );
                })}
            </div>

            {/* Scrollable tab content */}
            <div
                css={css`
                    flex: 1;
                    overflow-y: auto;
                    padding: 14px 20px 24px;
                    min-height: 0;
                `}
            >
                {showSpinner ? (
                    <div
                        css={css`
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            padding: 40px 0;
                        `}
                    >
                        <CircularProgress
                            size={22}
                            css={css`
                                color: ${greens[0]};
                            `}
                        />
                    </div>
                ) : !enriched ? (
                    <div
                        css={css`
                            font-family: ${FONT};
                            font-weight: 500;
                            font-size: 12.5px;
                            color: ${greys[1]};
                            font-style: italic;
                            padding: 8px 0;
                        `}
                    >
                        Order details unavailable.
                    </div>
                ) : tab === 'Details' ? (
                    <DetailsTab order={enriched} />
                ) : tab === 'Items/Docs' ? (
                    <DetailTabItemsAndDocs order={enriched} />
                ) : tab === 'Notes' ? (
                    <DetailTabNotes order={enriched} />
                ) : (
                    <DetailTabSurvey order={enriched} />
                )}
            </div>
        </div>
    );
}
